/**
 * Resolves a chained key that defines a value in
 * a nested object and returns the value
 * @param obj (Nested) object to get value from
 * @param chainedKey Possibly multiple keys chained by dots, e.g. 'modifier.scope.type'
 * @returns The value from `obj` defined by `chainedKey`,
 * `undefined` if any key in the chain is missing
 */
export function getValueByChainedKeys(obj: Record<string, any>, chainedKey: string): any {
  return chainedKey.split('.').reduce((acc, key) => acc?.[key], obj);
}

/**
 * Checks the "checkedObj" recursively for keys defined in the "referenceObj"
 * and returns a new object containing only those key-value pairs.
 *
 * @param {Record<string, any>} referenceObj  Object that defines which keys to keep
 * @param {Record<string, any>} checkedObj Object to be checked against the reference object
 * @returns {Record<string, any>} A new object containing only the keys present
 * in the reference object
 */
export function keepKeys(
  referenceObj: Record<string, any>,
  checkedObj: Record<string, any>
): Record<string, any> {
  const result: Record<string, any> = {};
  for (const key in referenceObj) {
    if (key in checkedObj) {
      const value = checkedObj[key];
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        // If the value is a nested object, recursively keep keys
        result[key] = keepKeys(referenceObj[key], value);
      } else {
        // Otherwise, keep the value as is
        result[key] = value;
      }
    }
  }
  return result;
}
